import React, { useState, useEffect } from "react"
import { Link, useSearchParams } from "react-router-dom"
import Nav from "../Nav"

const PER_PAGE = 50

export default function WorkersCompensationSubmissionsReport({ user }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [startDate, setStartDate] = useState(searchParams.get("start_date") || "")
  const [endDate, setEndDate] = useState(searchParams.get("end_date") || "")
  const [email, setEmail] = useState(searchParams.get("email") || "")
  const [page, setPage] = useState(parseInt(searchParams.get("page") || "1", 10))
  const [pagination, setPagination] = useState({ total_count: 0, total_pages: 0 })
  const [selectedSubmission, setSelectedSubmission] = useState(null)

  const updateSearchParams = (start, end, emailVal, pageNum) => {
    const params = new URLSearchParams()
    if (start) params.set("start_date", start)
    if (end) params.set("end_date", end)
    if (emailVal) params.set("email", emailVal)
    params.set("page", pageNum)
    setSearchParams(params)
  }

  const fetchData = (start, end, emailVal, pageNum = 1) => {
    setLoading(true)
    setError(null)

    const params = new URLSearchParams()
    if (start) params.append("start_date", start)
    if (end) params.append("end_date", end)
    if (emailVal) params.append("email", emailVal)
    params.append("page", pageNum)
    params.append("per_page", PER_PAGE)

    fetch(`/api/reports/workers_compensation_submissions?${params}`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json()
      })
      .then((json) => {
        setData(json.data || [])
        setPagination(json.pagination || {})
        setLoading(false)
      })
      .catch((err) => {
        setError(err.message)
        setLoading(false)
      })
  }

  useEffect(() => {
    fetchData(startDate, endDate, email, page)
  }, [])

  const handleApplyFilter = (e) => {
    e.preventDefault()
    setPage(1)
    updateSearchParams(startDate, endDate, email, 1)
    fetchData(startDate, endDate, email, 1)
  }

  const handleClearFilter = () => {
    setStartDate("")
    setEndDate("")
    setEmail("")
    setPage(1)
    updateSearchParams("", "", "", 1)
    fetchData("", "", "", 1)
  }

  const handlePageChange = (newPage) => {
    setPage(newPage)
    updateSearchParams(startDate, endDate, email, newPage)
    fetchData(startDate, endDate, email, newPage)
  }

  const handleDownloadCSV = () => {
    const params = new URLSearchParams()
    if (startDate) params.append("start_date", startDate)
    if (endDate) params.append("end_date", endDate)
    if (email) params.append("email", email)
    params.append("format", "csv")
    window.location.href = `/api/reports/workers_compensation_submissions?${params}`
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    return new Date(dateString).toLocaleDateString()
  }

  const field = (label, value) => (
    <div key={label}>
      <dt className="text-sm font-medium text-gray-600 mb-1">{label}</dt>
      <dd className="text-sm text-gray-900 break-words">
        {value === null || value === undefined
          ? "N/A"
          : typeof value === "boolean"
            ? value ? "Yes" : "No"
            : Array.isArray(value)
              ? value.length > 0
                ? typeof value[0] === "object"
                  ? value.map((item, i) => (
                      <div key={i} className="mb-1 pl-2 border-l-2 border-gray-200">
                        {Object.entries(item).map(([k, v]) => `${k}: ${v}`).join(" | ")}
                      </div>
                    ))
                  : value.join(", ")
                : "—"
              : String(value)}
      </dd>
    </div>
  )

  if (selectedSubmission) {
    const s = selectedSubmission
    return (
      <div className="min-h-screen bg-gray-100">
        <Nav user={user} />

        <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => setSelectedSubmission(null)}
            className="mb-6 text-blue-600 hover:text-blue-700 font-medium"
          >
            ← Back to List
          </button>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-5 py-6 sm:px-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Workers Compensation Submission</h2>
              <div className="mt-2 flex gap-6 flex-wrap">
                <p className="text-gray-600">ID: {s.id}</p>
                <p className="text-gray-600">
                  Hawksoft Client ID:{" "}
                  <span className="font-semibold text-gray-900">{s.hawksoft_client_id || "—"}</span>
                </p>
                {s.hawksoft_id && (
                  <p className="text-gray-600">
                    Hawksoft ID: <span className="font-semibold text-gray-900">{s.hawksoft_id}</span>
                  </p>
                )}
                {s.agent_assigned && (
                  <p className="text-gray-600">
                    Agent: <span className="font-semibold text-gray-900">{s.agent_assigned}</span>
                  </p>
                )}
              </div>
            </div>

            <div className="px-5 py-6 sm:px-6 space-y-8">

              {/* Contact */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                <dl className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {field("First Name", s.first_name)}
                  {field("Last Name", s.last_name)}
                  {field("Email", s.email)}
                  {field("Phone", s.phone)}
                  {field("Business Name", s.business_name)}
                  {field("SMS Consent", s.sms_consent)}
                </dl>
              </div>

              {/* Address */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Business Address</h3>
                <dl className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {field("Address", s.address)}
                  {field("City", s.city)}
                  {field("State", s.state)}
                  {field("Zip Code", s.zip_code)}
                </dl>
              </div>

              {/* Business Info */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Business Information</h3>
                <dl className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {field("Type of Business", s.type_of_business)}
                  {field("Annual Revenue", s.annual_revenue)}
                  {field("Number of Employees", s.number_of_employees)}
                  {field("Payroll Amount", s.payroll_amount)}
                  {field("Subcontractors With Insurance", s.subcontractors_with_insurance)}
                  {field("Subcontractors Without Insurance", s.subcontractors_without_insurance)}
                  {field("Federal ID", s.federal_id)}
                  {field("Number of Owners", s.number_of_owners)}
                  {field("Ownership Type", s.ownership_type)}
                  {field("Works in Multiple States", s.work_multiple_states)}
                </dl>
              </div>

              {/* Workers Compensation History */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Workers Compensation History</h3>
                <dl className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {field("Denied Workers Compensation", s.denied_workers_compensation)}
                  {field("Currently Has Workers Compensation", s.currently_have_workers_compensation)}
                  {field("Years With Current Insurer", s.years_with_current_insurer)}
                  {field("Current Carrier", s.current_carrier)}
                  {field("Claims Last 3 Years", s.claims_last_three_years)}
                  {field("Explain Claims", s.explain_claims)}
                </dl>
              </div>

              {/* Additional Insured */}
              {s.additional_insured && s.additional_insured.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Insured</h3>
                  <dl className="grid grid-cols-1 gap-6">
                    {field("Additional Insured", s.additional_insured)}
                  </dl>
                </div>
              )}

              {/* Also Interested In */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Coverage Interest</h3>
                <dl className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {field("Also Interested In", s.also_interested_in)}
                </dl>
              </div>

              {/* Timestamps */}
              <div className="pt-4 border-t border-gray-200">
                <dl className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <dt className="text-sm font-medium text-gray-500 mb-1">Submitted</dt>
                    <dd className="text-sm text-gray-600">
                      {s.created_at ? new Date(s.created_at).toLocaleString() : "N/A"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 mb-1">Last Updated</dt>
                    <dd className="text-sm text-gray-600">
                      {s.updated_at ? new Date(s.updated_at).toLocaleString() : "N/A"}
                    </dd>
                  </div>
                </dl>
              </div>

            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Nav user={user} />

      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center gap-2">
          <Link to="/" className="text-blue-600 hover:text-blue-700">
            Dashboard
          </Link>
          <span className="text-gray-400">/</span>
          <span className="text-gray-600">Workers Compensation Submissions</span>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-5 py-6 sm:px-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Workers Compensation Submissions</h2>
                <p className="mt-2 text-gray-600">
                  Total: {pagination.total_count || 0} submission{pagination.total_count !== 1 ? "s" : ""}
                </p>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="px-5 py-6 sm:px-6 border-b border-gray-200 bg-gray-50">
            <form onSubmit={handleApplyFilter} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:items-end">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="text"
                    placeholder="Search by email..."
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Submitted Date From</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Submitted Date To</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
                  >
                    Apply Filters
                  </button>
                  <button
                    type="button"
                    onClick={handleClearFilter}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 font-medium"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleDownloadCSV}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium text-sm"
                >
                  Download CSV
                </button>
              </div>
            </form>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {loading && (
              <div className="px-6 py-8 text-center text-gray-600">Loading...</div>
            )}
            {error && (
              <div className="px-6 py-8 text-center text-red-600">Error: {error}</div>
            )}
            {!loading && !error && data?.length === 0 && (
              <div className="px-6 py-8 text-center text-gray-600">No data available</div>
            )}

            {!loading && !error && data?.length > 0 && (
              <>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900 w-1/5 min-w-40">Email</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900 w-1/5 min-w-32">Name</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900 w-1/5 min-w-36">Business</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900 w-1/6 min-w-24">City / State</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900 w-1/6 min-w-28">Agent</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900 w-1/6 min-w-28">Submitted</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {data.map((row) => (
                      <tr
                        key={row.id}
                        onClick={() => setSelectedSubmission(row)}
                        className="hover:bg-blue-50 cursor-pointer transition-colors"
                      >
                        <td className="px-4 py-3 text-gray-900 truncate" title={row.email}>{row.email}</td>
                        <td className="px-4 py-3 text-gray-900 truncate" title={`${row.first_name} ${row.last_name}`}>
                          {row.first_name} {row.last_name}
                        </td>
                        <td className="px-4 py-3 text-gray-900 truncate" title={row.business_name || ""}>{row.business_name || "—"}</td>
                        <td className="px-4 py-3 text-gray-700 truncate">
                          {[row.city, row.state].filter(Boolean).join(", ") || "—"}
                        </td>
                        <td className="px-4 py-3 text-gray-700 truncate">{row.agent_assigned || "—"}</td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(row.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                  <div className="text-sm text-gray-600">
                    Page {page} of {pagination.total_pages || 1}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePageChange(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(Math.min(pagination.total_pages, page + 1))}
                      disabled={page === pagination.total_pages}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
